import produce from 'immer';
import { DateTime } from 'luxon';

import {
  DELETED_KEY_REGEX,
  MAX_KEY_CHARACTER_LENGTH,
  MAX_NAME_CHARACTER_LENGTH,
  KEY_DISALLOWED_CHARACTERS,
} from 'constants/flags';
import { SubMetric } from 'types/backend/models/SubMetric';
import { isPlainObject } from 'lodash';

export {
  VALIDATION_SCHEMA_CONSTANTS,
  CONSTANTS,
} from 'constants/validation_constants';

interface EventProperty {
  condition_operator: string;
  data_type: string;
  name: string;
  value: string;
}

const UNIQUE_ID_WITH_DELIMITER_CHARACTER_LENGTH = 6;

/**
 * @description Adds a short unique key to end of the provided string
 * @see https://github.com/optimizely/flags/blob/master/frontend/EDGE_CASE.md#key-collisions
 * @example computeDeletedKeyWithUniqueId("my_rule_key") => "my_rule_key!deleted_1601910762830"
 */
export const computeDeletedKeyWithUniqueId = function(value?: string) {
  return `${value}!deleted_${Date.now()}`;
};

/**
 * @description Given a value with a concatenated unique key from computeDeletedKeyWithUniqueId, returns the regular value
 * @see https://github.com/optimizely/flags/blob/master/frontend/docs/decisions/0016-variations-page-performance.md
 * @example
 *  dropDeletedKeyUniqueId("my_rule_key!deleted_1601910762830") => "my_rule_key"
 *  dropDeletedKeyUniqueId("my_rule_key") => "my_rule_key"
 */
export const dropDeletedKeyUniqueId = function(value: string) {
  return value.split(DELETED_KEY_REGEX)[0];
};

/**
 * @description Util to append `_copy${++num}` i.e _copy with incrementing number to the end of string
 * @example
 *  duplicateCopyString(my_first_key) returns 'my_first_key_copy1',
 *  duplicateCopyString(my_first_name_copy1) returns 'my_first_name_copy2',
 *  duplicateCopyString(my_first_name_copy2) returns 'my_first_name_copy3',
 *  duplicateCopyString(my_copy1_first_name_copy1) returns 'my_copy1_first_name_copy2',
 *  duplicateCopyString(my_first_name_copy_1) returns 'my_first_name_copy_1_copy1',
 */
const duplicateCopyString = (str: string) => {
  const hasCopySuffix = /_copy\d+$/.test(str);
  if (!hasCopySuffix) {
    str += '_copy1';
    return str;
  }
  return str.replace(
    /(_copy)(\d+)$/,
    (_, prefix, num) => prefix + (parseInt(num) + 1)
  );
};

/**
 * @description Util to append `_new${++num}` i.e _new with incrementing number to the end of string
 * @example
 *  appendNewString(my_first_key) returns 'my_first_key_new1',
 *  appendNewString(my_first_name_new1) returns 'my_first_name_new2',
 *  appendNewString(my_first_name_new2) returns 'my_first_name_new3',
 *  appendNewString(my_new1_first_name_copy1) returns 'my_new1_first_name_new2',
 *  appendNewString(my_first_name_new_1) returns 'my_first_name_new_1_new1',
 */
const appendNewString = (str: string) => {
  const hasNewSuffix = /_new\d+$/.test(str);
  if (!hasNewSuffix) {
    str += '_new1';
    return str;
  }
  return str.replace(
    /(_new)(\d+)$/,
    (_, prefix, num) => prefix + (parseInt(num) + 1)
  );
};

/**
 * @description Util to ensure key and name are unique
 * @see https://github.com/optimizely/flags/blob/master/frontend/docs/decisions/0016-variations-page-performance.md
 * @example
 *  ensureUniqueValues({
 *    existingValues: { "my_first_key": { "key": "my_first_key", "name": "My First Key" } }
 *    key: 'my_first_key',
 *  }) => ({
 *    newKey: 'my_first_key_new1',
 *  });
 *  ensureUniqueValues({
 *    existingValues: { "my_first_key": { "key": "my_first_key", "name": "My First Key" } }
 *    name: 'My First Key',
 *  }) => ({
 *    newKey: 'my_first_key_new1',
 *    newName: 'My First Key_new1',
 *  });
 *  ensureUniqueValues({
 *    existingValues: { "my_first_key": { "key": "my_first_key", "name": "My First Key" } }
 *    name: 'My First Key',
 *    key: 'my_first_key',
 *  }) => ({
 *    newKey: 'my_first_key_new1',
 *    newName: 'My First Key_new1',
 *  });
 *  ensureUniqueValues({
 *    existingValues: { "my_first_key": { "key": "my_first_key", "name": "My First Key" } }
 *    isDuplicate: true,
 *    name: 'My First Key',
 *    key: 'my_first_key',
 *  }) => ({
 *    newKey: 'my_first_key_copy1',
 *    newName: 'My First Key_copy1',
 *  });
 *  ensureUniqueValues({
 *    existingValues: { "my_first_key": { "key": "my_first_key", "name": "My First Key" } }
 *    name: 'An Original Key',
 *  }) => ({
 *    newKey: 'an_original_key',
 *    newName: 'An Original Key',
 *  });
 */
export function ensureUniqueValues({
  existingValues,
  isDuplicate,
  key,
  name,
}: {
  existingValues?: { [key: string]: { key?: string; name?: string } };
  isDuplicate?: boolean;
  key?: string;
  name?: string;
}): [string, string] {
  let newName = '';
  if (name) {
    newName = isDuplicate ? duplicateCopyString(name) : name;
    const names = Object.values(existingValues || {}).map(({ name }) => name);

    while (names.includes(newName)) {
      newName = isDuplicate
        ? duplicateCopyString(newName)
        : appendNewString(newName);
    }
    newName = newName.slice(0, MAX_NAME_CHARACTER_LENGTH);
  }

  let newKey = '';
  if (key) {
    newKey = newName
      ? formatKey(newName)
      : isDuplicate
      ? formatKey(duplicateCopyString(key))
      : formatKey(key);
    const keys = Object.values(existingValues || {}).map(({ key }) => key);
    while (keys.includes(newKey)) {
      newKey = formatKey(
        isDuplicate ? duplicateCopyString(newKey) : appendNewString(newKey)
      );
    }
  }
  return [newKey, newName];
}

/**
 * @description Format date to user friendly string in user's local timezone
 */
export const formatDateShort = (date: Date): string => {
  return DateTime.fromJSDate(date, {
    zone: 'utc',
  })
    .toLocal()
    .toFormat('DD');
};

/**
 * @description Format date to user friendly string in user's local timezone
 */
export const formatDateTimeShort = (date: Date): string => {
  return DateTime.fromJSDate(date, {
    zone: 'utc',
  })
    .toLocal()
    .toFormat('MMM d, h:mm a');
};

/**
 * @description Given a string returns, replaces all non-alphanumeric with underscores ("_")
 * @example
 *  formatKey('Key hypothetically less than 64 characters!') => 'key_hypothetically_less_than_64_characters_'
 *  formatKey('Key hypothetically longer than 64 characters!') => 'key_hypothetically_longer_than_64_cha_6f6s2'
 */
export function formatKey(
  key: string,
  allowUppercase: boolean = false
): string {
  let formattedKey = key.replace(KEY_DISALLOWED_CHARACTERS, '_');

  if (!allowUppercase) {
    formattedKey = formattedKey.toLowerCase();
  }

  if (formattedKey.length >= MAX_KEY_CHARACTER_LENGTH) {
    formattedKey = generateOrConcatenateUniqueId([
      formattedKey.slice(
        0,
        MAX_KEY_CHARACTER_LENGTH - UNIQUE_ID_WITH_DELIMITER_CHARACTER_LENGTH
      ),
      '_',
    ]);
  }
  return formattedKey;
}

/**
 * Given a percentage_included value from a Rule or RuleVariation,
 * return a string that can be displayed to the user.
 * percentage_included in the API is an integer from 0 to 10000 inclusive.
 * @param {Number} percentageIncluded
 * @returns {String}
 */
export function formatPercentageIncluded(
  percentageIncluded: number,
  auto?: boolean
): string {
  return (
    parseFloat(percentageIncluded + 'e-2').toString() +
    `${auto ? ' (Auto)' : ''}`
  );
}

/**
 * @description Util to get the hash value (sans #) from location.hash
 */
export function getHash(location: { hash: string }): string | undefined {
  return location.hash.split('#')[1];
}

/**
 * @description Adds a short unique key to end of the provided string(s)
 * @see https://github.com/optimizely/flags/blob/master/frontend/EDGE_CASE.md#key-collisions
 * @example
 *  generateOrConcatenateUniqueId() => "6f6s2"
 *  generateOrConcatenateUniqueId(['_']) => "_6f6s2"
 *  generateOrConcatenateUniqueId(['my_rule_key', '_']) => "my_rule_key_6f6s2"
 */
export const generateOrConcatenateUniqueId = function(
  valuesToConcatenate: string[] = []
) {
  const fiveCharacterUniqueId = 'xfxsx'
    .replace(/[x]/g, c => {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    })
    .toLowerCase();
  return valuesToConcatenate.join('') + fiveCharacterUniqueId;
};

/**
 * @description maps object values to object keys
 */
export function mapValues(obj: any, callback: any) {
  const entries = Object.entries(obj).map(([k, v]) => [k, callback(k, v)]);
  return entries.reduce((result, entry) => {
    const [k, v] = entry;
    result[k] = v;
    return result;
  }, {} as { [k: string]: any });
}

/**
 * @description Given a string value taken from the rollout traffic input, return the
 *  corresponding value of the percentage_included property of the rollout rule
 *  in the feature API entity.
 *  The traffic input shows 0 to 100 inclusive.
 *  percentage_included in the API is an integer from 0 to 10000 inclusive.
 */
export function parseTraffic(traffic: string): number {
  // Using Math.round because multiplying by 100 yields a slightly-off result
  // for some numbers
  return Math.round((parseFloat(traffic) || 0) * 100);
}

/**
 * @description Util to remove a key from a provided object without mutating the original reference
 */
export function removeKey(
  obj: {
    [key: string]: any;
  },
  key: string
) {
  return produce(obj, draft => {
    delete draft[key];
    return draft;
  });
}

/**
 * @description Given a click event, the fn will not be called if any of the following items are pressed
 * - event.button: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button (middle mouse button - Windows / Mac)
 * - event.ctrlKey: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/ctrlKey (control key - Windows)
 * - event.metaKey: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/metaKey (command key - Mac)
 * - event.shiftKey: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/shiftKey (shift key - Windows / Mac)
 */
export function shouldPreventDefaultAndCallFn(
  fn: (event: MouseEvent) => void
): (event: MouseEvent) => void {
  return event => {
    const { button, ctrlKey, metaKey, shiftKey } = event;
    const MIDDLE_BUTTON = 1;
    if (button === MIDDLE_BUTTON || ctrlKey || shiftKey || metaKey) {
      return;
    }
    // preventDefault is a method that must be invoked on an event object to know which default to prevent
    // When using a destructured preventDefault(),it just accesses the value and does not automatically bind any functions
    // See more at https://stackoverflow.com/questions/49616305/destructing-ev-preventdefault
    event.preventDefault();
    fn(event);
  };
}

/**
 * @description Sorting function that will return items in ascending order of
 * value. Can be used for integers, epoch time, etc.
 */
export const sortAsc = (a: any, b: any): number => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

/**
 * @description Function that will return boolean true if the user's plan_id is free_rollouts .
 */
export const isFreeRolloutsUser = (): boolean => {
  // @ts-ignore
  const accountInfo = window?.optlyConfig?.account_info || {};
  return accountInfo.plan_id === 'free_rollouts';
};

export const getUserAccountInfo = (): {
  id: string;
  name: string;
  role: string;
} => {
  // @ts-ignore
  const accountInfo = window?.optlyConfig?.account_info || {};
  const currentUser = Object.keys(accountInfo).length
    ? accountInfo.user_accounts?.find(
        (uAccount: any) => uAccount.current_account === true
      )
    : undefined;

  return {
    id: accountInfo.account_id,
    name: currentUser?.account_name || '',
    role: currentUser?.role || '',
  };
};
/**
 * @description getCurrentUser email
 */
export const getCurrentUser = (): string | null => {
  // @ts-ignore
  const accountInfo = window?.optlyConfig?.account_info || {};
  const currentUser = Object.keys(accountInfo).length
    ? accountInfo.user_accounts.find(
        (uAccount: any) => uAccount.current_account === true
      )
    : undefined;

  return currentUser?.email || null;
};

export const getCurrentLoginUserEmail = (): string | null => {
  // @ts-ignore
  const accountInfo = window?.optlyConfig?.account_info || {};
  const currentUser = Object.keys(accountInfo).length
    ? accountInfo.email
    : undefined;

  return currentUser || null;
};
/**
 * @description Converts string to Title Case (e.g. 'foo bar baz' becomes 'Foo Bar Baz')
 */
export const toTitleCase = (str: string): string => {
  return str.replace(/\w\S*/g, (txt: string) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

/**
 * @description Truncates a string when given string is longer than n characters.
 * Use 'truncate' class instead this function when possible.
 */
export const truncate = (str: string, n: number): string => {
  return str.length > n ? str.slice(0, n - 1) + '...' : str;
};

/**
 * @description Utility to check if an object is a "simple" object, meaning it is a plain object with no nested objects.
 * A simple object is defined as an object that is not `null`, an array, or a more complex object like `Date`, `Map`, `Set`, etc.
 * The function returns `true` if the object does not contain any nested objects and is a plain object.
 * @example
 *  isSimpleObject({ a: 1, b: 2 }) => true
 *  isSimpleObject({ a: 1, b: { c: 2 } }) => false
 *  isSimpleObject([1, 2, 3]) => false
 *  isSimpleObject(null) => false
 *  isSimpleObject(undefined) => false
 *  isSimpleObject(new Date()) => false
 *  isSimpleObject(new Map()) => false
 *  isSimpleObject(new Set()) => false
 *  isSimpleObject(Object.create(null)) => true
 */

export const isSimpleObject = (obj: any): boolean => {
  // Ensure obj is a plain object and not null
  if (!isPlainObject(obj)) {
    return false;
  }
  // Check for nested objects
  for (const key in obj) {
    if (isPlainObject(obj[key])) {
      return false; // Found a nested object
    }
  }
  return true; // No nested objects found, so it's a simple object
};

export const formatMetricsForMetricsHub = (metrics: any) => {
  const formattedMetrics = metrics.map((metric: any) => {
    let updatedMetric = metric;

    if (metric.aggregator !== 'sum' && metric.hasOwnProperty('field')) {
      const { field, ...rest } = metric;
      updatedMetric = rest;
    }
    if (updatedMetric.metrics) {
      const subMetricsWithoutScope: SubMetric[] = metric.metrics.map(
        (subMetric: SubMetric) => {
          if (subMetric.hasOwnProperty('scope')) {
            const { scope, ...rest } = subMetric;
            subMetric = rest;
          }
          return subMetric;
        }
      );
      updatedMetric = {
        ...updatedMetric,
        metrics: subMetricsWithoutScope,
      };
    }
    if (
      updatedMetric.event_properties &&
      !updatedMetric.event_properties.hasOwnProperty('filter')
    ) {
      const formattedConditions = updatedMetric.event_properties.map(
        ({ name, value, data_type, condition_operator }: EventProperty) => ({
          name,
          operator: condition_operator,
          type: data_type,
          value,
        })
      );
      updatedMetric = {
        ...updatedMetric,
        event_properties: {
          filter: {
            combine_operator: updatedMetric.filter_combine_operator,
            conditions: formattedConditions,
          },
        },
      };
    }
    return {
      ...updatedMetric,
      scope: 'visitor',
    };
  });
  return formattedMetrics;
};

const utils = {
  computeDeletedKeyWithUniqueId,
  dropDeletedKeyUniqueId,
  ensureUniqueValues,
  formatDateShort,
  formatKey,
  formatMetricsForMetricsHub,
  formatPercentageIncluded,
  generateOrConcatenateUniqueId,
  getCurrentLoginUserEmail,
  getCurrentUser,
  getHash,
  getUserAccountInfo,
  isFreeRolloutsUser,
  isSimpleObject,
  mapValues,
  parseTraffic,
  removeKey,
  shouldPreventDefaultAndCallFn,
  sortAsc,
  toTitleCase,
  truncate,
};

export default utils;
